/**
 * GapNote MDXEditor Plugin
 *
 * Renders a private note block that is marked as "Only visible to you".
 * The content inside the note is editable.
 *
 * MARKDOWN SYNTAX (containerDirective):
 * :::GapNote
 * Your note content here
 * :::
 */

import { DirectiveDescriptor, DirectiveEditorProps, usePublisher, insertDirective$, NestedLexicalEditor } from '@mdxeditor/editor'
import type { ContainerDirective } from 'mdast-util-directive'
import type { RootContent } from 'mdast'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Tooltip from '@mui/material/Tooltip'
import { useThemeStore } from 'theme/ThemeStore'

const DIRECTIVE_NAME = 'GapNote'

const noteColors = {
   light: {
      background: '#FEF9E7',
      border: '#D4AC0D',
      text: '#5D4E37',
      headerText: '#4A3728',
   },
   dark: {
      background: '#2A2517',
      border: '#B8860B',
      text: '#D4C5A0',
      headerText: '#E8DCC0',
   },
}

const getContent = (node: ContainerDirective) => node.children

const getUpdatedMdastNode = (node: ContainerDirective, children: RootContent[]): ContainerDirective => ({
   ...node,
   children: children as ContainerDirective['children'],
})

function GapNoteComponent(_props: DirectiveEditorProps) {
   const colors = noteColors[useThemeStore().dark ? 'dark' : 'light']

   return (
      <Box
         sx={{
            backgroundColor: colors.background,
            border: `2px solid ${colors.border}`,
            borderRadius: 2,
            p: 2,
            my: 2,
         }}
      >
         <Box
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               mb: 1.5,
            }}
         >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               <DescriptionOutlinedIcon sx={{ fontSize: 20, color: colors.headerText }} />
               <Typography
                  variant="subtitle2"
                  sx={{
                     fontWeight: 700,
                     color: colors.headerText,
                  }}
               >
                  Note
               </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
               <LockOutlinedIcon sx={{ fontSize: 16, color: colors.text }} />
               <Typography
                  variant="caption"
                  sx={{
                     color: colors.text,
                  }}
               >
                  Only visible to you
               </Typography>
            </Box>
         </Box>
         <Box
            sx={{
               color: colors.text,
               fontSize: '1.1rem',
               '& > div': {
                  minHeight: '1.5em',
               },
               '& p': {
                  margin: 0,
               },
               '& [class*="_nestedEditor"], & [class*="_editorRoot"], & [class*="_contentEditable"]': {
                  backgroundColor: 'transparent',
               },
            }}
         >
            <NestedLexicalEditor<ContainerDirective>
               block
               getContent={getContent}
               getUpdatedMdastNode={getUpdatedMdastNode}
            />
         </Box>
      </Box>
   )
}

export const GapNoteDirectiveDescriptor: DirectiveDescriptor = {
   name: DIRECTIVE_NAME,
   type: 'containerDirective',
   testNode(node) {
      return node.name === DIRECTIVE_NAME
   },
   attributes: [],
   hasChildren: true,
   Editor: GapNoteComponent,
}

export function InsertGapNote() {
   const insertDirective = usePublisher(insertDirective$)

   const handleInsertClick = () => {
      insertDirective({
         name: DIRECTIVE_NAME,
         type: 'containerDirective',
      } as Parameters<typeof insertDirective>[0] & { children?: unknown })
   }

   return (
      <Tooltip title="Insert private note">
         <IconButton size="small" onClick={handleInsertClick}>
            <DescriptionOutlinedIcon fontSize="small" />
         </IconButton>
      </Tooltip>
   )
}
